import { Pagination } from '@mui/material';
import { Tracking } from '@vromo/analytics/tracking';
import { useStore } from '@vromo/mobx-react';
import { LocalStorage } from '@vromo/models';
import Button from '@vromo/ui/components/Button';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import LoadingIndicator from '@vromo/ui/components/LoadingIndicator';
import Table from '@vromo/ui/components/Table';
import { RootStore } from 'dashboard/src/stores/RootStore';
import { observer } from 'mobx-react-lite';
import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RatesWrapper, Segment, SegmentRow, SegmentTitle } from './Components';
import DriverRow from './components/DriverRow';
import RateInputSection from './components/RateInputSection';
import TableHeaders from './components/TableHeaders';
import styles from './driverReport.style';
import { Rate } from './helpers';
import useFetchDriverReport from './useFetchDriverReport';
import { Filters } from '../utils/reportTypes';

interface Props {
    csvTitle: string;
    filters: Filters;
}

const DriverReport = observer<Props>(({ csvTitle, filters }) => {
    const {
        authStore: { role }
    } = useStore<RootStore>('rootStore');
    const { id: groupId } = role.group || {};

    const { t } = useTranslation();
    const getLocale = useCallback(
        (key: string, params?: { unit?: string }) => t(`driverReport.${key}`, params),
        [t]
    );

    useEffect(() => Tracking.action('Driver Report tab opened.'), []);

    const { viewStore } = useStore<RootStore>('rootStore');

    const [filterRates, setFilterRates] = useState<Rate>(
        LocalStorage?.driverReportPayRates
            ? LocalStorage.driverReportPayRates
            : {
                  fixed: 0.0,
                  perCompletedJob: 0.0,
                  perCancelled: 0.0,
                  perKmJob: 0.0,
                  perKmEmpty: 0.0
              }
    );

    const {
        applyFilters,
        downloadCSV,
        isLoading,
        isLoadingCSV,
        pageNumber,
        totalPages,
        vehicleReports
    } = useFetchDriverReport({
        filters,
        filterRates,
        groupId
    });

    const updateRate = useCallback((rateField: string, val: string) => {
        const valueAsFloat = parseFloat(val);
        setFilterRates((prevRates) => ({ ...prevRates, [rateField]: valueAsFloat }));
    }, []);

    const createDriverCsv = async () => {
        Tracking.action('Driver Report CSV Downloaded');
        await downloadCSV(csvTitle);
    };

    const distanceUnit = viewStore.imperialUnits ? 'mi' : 'km';
    const unitParam = {
        unit: viewStore.imperialUnits ? 'mile' : 'Km'
    };

    const onInputChange = useCallback((e) => updateRate(e.target.id, e.target.value), [updateRate]);

    const onApplyFilters = async (newPageNumber?: number) => await applyFilters(newPageNumber);

    const onChangePageNumber = (_: React.ChangeEvent<unknown>, p: number) => onApplyFilters(p - 1);

    useEffect(() => {
        onApplyFilters();
    }, [filters.vehicles, filters.teams, filters.startDate, filters.endDate]);

    return (
        <div style={styles.container}>
            <SegmentRow>
                <Segment>
                    <SegmentTitle>
                        <h4>{getLocale('setPayRates')}</h4>
                    </SegmentTitle>
                    <RatesWrapper style={styles.ratesWrapper}>
                        <RateInputSection
                            label={getLocale('fixed')}
                            id={'fixed'}
                            testId={'driverreport-rates-fixed'}
                            value={filterRates.fixed}
                            onChange={onInputChange}
                            disabled={isLoading}
                        />
                        <RateInputSection
                            label={getLocale('perCompleteJob')}
                            id={'perCompletedJob'}
                            testId={'driverreport-rates-perCompletedJob'}
                            value={filterRates.perCompletedJob}
                            onChange={onInputChange}
                            disabled={isLoading}
                        />
                        <RateInputSection
                            label={getLocale('perCancelled')}
                            id={'perCancelled'}
                            testId={'driverreport-rates-perCancelled'}
                            value={filterRates.perCancelled}
                            onChange={onInputChange}
                            disabled={isLoading}
                        />
                        <RateInputSection
                            label={getLocale('perKmJob', unitParam)}
                            id={'perKmJob'}
                            testId={'driverreport-rates-perKmJob'}
                            value={filterRates.perKmJob}
                            onChange={onInputChange}
                            disabled={isLoading}
                        />
                        <RateInputSection
                            label={getLocale('perKmEmpty', unitParam)}
                            id={'perKmEmpty'}
                            testId={'driverreport-rates-perKmEmpty'}
                            value={filterRates.perKmEmpty}
                            onChange={onInputChange}
                            disabled={isLoading}
                        />
                    </RatesWrapper>
                    <Button
                        style={styles.applyFiltersButton}
                        disabled={isLoading}
                        onClick={() => onApplyFilters(pageNumber)}>
                        {getLocale('applyFilters')}
                    </Button>
                    <SegmentTitle>
                        <h4>{getLocale('driverDetails')}</h4>
                        <Button
                            style={styles.exportCSVButton}
                            disabled={isLoading || isLoadingCSV || !vehicleReports}
                            loading={isLoadingCSV}
                            onClick={createDriverCsv}>
                            {getLocale('exportCSV')}
                            <FontAwesome style={styles.exportIcon} icon="download" />
                        </Button>
                    </SegmentTitle>
                    {!isLoading && !!vehicleReports && (
                        <>
                            <div style={styles.tableContainer as CSSProperties}>
                                <Table>
                                    <TableHeaders {...{ distanceUnit, getLocale }} />
                                    <tbody>
                                        {vehicleReports.map((vehicle) => (
                                            <DriverRow {...vehicle} />
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            {totalPages > 1 && (
                                <Pagination
                                    variant="outlined"
                                    shape="rounded"
                                    count={totalPages}
                                    page={pageNumber + 1}
                                    onChange={onChangePageNumber}
                                />
                            )}
                        </>
                    )}
                    {!vehicleReports && isLoading && (
                        <div style={styles.loadingContainer}>
                            {getLocale('loading')} <LoadingIndicator />
                        </div>
                    )}
                    {!vehicleReports && !isLoading && (
                        <div style={styles.loadingContainer}>{getLocale('noResults')}</div>
                    )}
                </Segment>
            </SegmentRow>
        </div>
    );
});

export default DriverReport;
